import { DateTime } from '@ksyos/date-time';
import { Box, PageHeader } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { uniq } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { IOrganization } from '@frontend/components/interfaces';
import type { ICompensationPayout } from '@frontend/components/interfaces/compensationPayout';
import type { IProvidedServicesAndCompensationPayouts } from '@frontend/components/interfaces/providedServicesAndCompensationPayouts';
import type { IServiceConsumer } from '@frontend/components/interfaces/serviceConsumer';
import type { IServiceOrganizationSettings } from '@frontend/components/interfaces/serviceOrganizationSettings';
import type { IServiceProvider } from '@frontend/components/interfaces/serviceProvider';
import type { IServiceSupervisor } from '@frontend/components/interfaces/serviceSupervisor';
import { ActivityType, CompensationPayoutEntity, ManagerAuthorization, ServiceCode, ServiceType } from '@common/enums';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { SettingsCategoryFilterButtonRow } from '@frontend/components/elements/SettingsCategoryFilterButtonRow';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, useAbortableFetch } from '@frontend/utils';
import { ActivityCompensationPayoutsAddEdit } from './ActivityCompensationPayoutsAddEdit';
import { ActivityCompensationPayoutsHistory } from './ActivityCompensationPayoutsHistory';
import { ServiceActivityCompensationPayouts } from './ServiceActivityCompensationPayouts';
import { ServiceActivityCompensationPayoutsHistory } from './ServiceActivityCompensationPayoutsHistory';

type ActivityCompensationPayoutsListProps = {
    organizationId?: string;
};

export function ActivityCompensationPayoutsList(props: ActivityCompensationPayoutsListProps) {
    const abortableFetch = useAbortableFetch();
    const {
        employmentId,
        organizationId: urlOrganizationId,
        episodeCategory,
    } = useParams() as {
        employmentId: string;
        episodeCategory: EpisodeCategory;
        organizationId: string;
    };

    const organizationId = urlOrganizationId || props.organizationId || '';

    useEffect(() => {
        setIsLoading(true);
        load(true);
    }, [episodeCategory, employmentId, organizationId]);

    const [providedServicesWithCompensationsPayouts, setProvidedServicesWithCompensationsPayouts] =
        useState<IProvidedServicesAndCompensationPayouts>();
    const [higherLevelCompensationsPayouts, setHigherLevelCompensationsPayouts] =
        useState<IProvidedServicesAndCompensationPayouts>();
    const [selectedServiceGroupTab, setSelectedServiceGroupTab] = useState<{ displayText: string; id: string }>();
    const [serviceGroupNames, setServiceGroupNames] = useState<string[]>();
    const [organization, setOrganization] = useState<IOrganization | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    if (employmentId) {
        assertHasAuthorization(ManagerAuthorization.employmentSettingsRead);
    } else {
        assertHasAuthorization(ManagerAuthorization.organizationRead);
    }

    if (!employmentId && !organizationId) {
        return null;
    }

    return (
        <>
            <PageHeader
                title={`${translateKey('compensationPayouts.fees')} ${translateKey(
                    `episodeCategoryEnum.${episodeCategory}`
                )}`}
            />

            {renderTabs()}

            {!isLoading && renderActivities()}
        </>
    );

    function renderTabs() {
        if (!serviceGroupNames) {
            return null;
        }

        return (
            <Box mb={8}>
                <SettingsCategoryFilterButtonRow
                    collection={serviceGroupNames.map((name) => ({ displayText: name, id: name }))}
                    onClick={setSelectedServiceGroupTab}
                    selectedItem={selectedServiceGroupTab}
                />
            </Box>
        );
    }

    async function load(hasEpisodeCategoryChanged: boolean = false) {
        const organizationUrl = `/api/organizations/${organizationId}`;
        const organizationResult = await abortableFetch.fetchJson(organizationUrl, 'GET');
        setOrganization(organizationResult.json);

        const url = new UrlBuilder('/api/compensationPayouts/providedServicesAndCompensationPayouts');
        url.add('excludeServiceTypeOther', true);
        url.add('episodeCategory', episodeCategory);

        const baseUrl = url.build();
        const urlWithEmploymentId = `${baseUrl}&employmentId=${employmentId}`;
        const urlWithOrganizationId = `${baseUrl}&organizationId=${organizationId}`;

        const result = await abortableFetch.fetchJson(
            employmentId ? urlWithEmploymentId : urlWithOrganizationId,
            'GET'
        );
        const providedServices: IProvidedServicesAndCompensationPayouts = result.json.item;

        sortByServiceName(providedServices.serviceConsumers);
        sortByServiceName(providedServices.serviceProviders);
        sortByServiceName(providedServices.serviceSupervisors);
        sortByServiceName(providedServices.serviceEmployments);

        const newServiceGroupNames = getServiceGroupNames(providedServices);

        if (!selectedServiceGroupTab || hasEpisodeCategoryChanged) {
            setSelectedServiceGroupTab({ displayText: newServiceGroupNames[0], id: newServiceGroupNames[0] });
        }

        let updatedHigherLevelCompensationPayouts: IProvidedServicesAndCompensationPayouts | undefined;
        // Group settings are inside providedServices for organizations
        if (organizationId) {
            updatedHigherLevelCompensationPayouts = providedServices;
        }

        if (employmentId) {
            const resultHigherLevel = await abortableFetch.fetchJson(urlWithOrganizationId, 'GET');
            updatedHigherLevelCompensationPayouts = resultHigherLevel.json.item;
        }
        setHigherLevelCompensationsPayouts(updatedHigherLevelCompensationPayouts);
        setServiceGroupNames(newServiceGroupNames);
        setProvidedServicesWithCompensationsPayouts(providedServices);

        setIsLoading(false);
    }

    function getServiceGroupNames(data: IProvidedServicesAndCompensationPayouts) {
        if (!data) {
            return [];
        }

        const sortedNames = Array.from(
            new Set<string>(data.serviceGroupMemberships.map((item) => item.serviceGroup.name).sort())
        );
        sortedNames.push('Logboek');

        return sortedNames;
    }

    function renderActivities() {
        if (!selectedServiceGroupTab || !providedServicesWithCompensationsPayouts || !serviceGroupNames) {
            return null;
        }

        let currentServiceGroupNames: string[];
        const tabContents: JSX.Element[] = [];

        if (selectedServiceGroupTab.displayText !== 'Logboek') {
            currentServiceGroupNames = [selectedServiceGroupTab.displayText];
        } else {
            currentServiceGroupNames = [...serviceGroupNames];
        }

        currentServiceGroupNames
            .filter((item) => item !== 'Logboek')
            .forEach((serviceGroupName) => {
                const servicesInGroup = providedServicesWithCompensationsPayouts.serviceGroupMemberships
                    .filter((serviceGroupMembership) => serviceGroupMembership.serviceGroup.name === serviceGroupName)
                    .map((serviceGroupMembership) => serviceGroupMembership.service.id);

                const serviceConsumers = providedServicesWithCompensationsPayouts.serviceConsumers.filter(
                    (serviceConsumer) => servicesInGroup.includes(serviceConsumer.service.id)
                );

                const biometricianServiceProviders: IServiceProvider[] = [];
                const graderServiceProvider = [];
                const tooServiceProvider = [];
                const specialistServiceProviders = [];

                const serviceProviders = providedServicesWithCompensationsPayouts.serviceProviders.filter(
                    (serviceProvider) => servicesInGroup.includes(serviceProvider.service.id)
                );

                const serviceSupervisors = providedServicesWithCompensationsPayouts.serviceSupervisors.filter(
                    (serviceSupervisor) => servicesInGroup.includes(serviceSupervisor.service.id)
                );

                const serviceEmployments = providedServicesWithCompensationsPayouts.serviceEmployments.filter(
                    (serviceEmployment) => servicesInGroup.includes(serviceEmployment.service.id)
                );

                const serviceOrganizationSettings =
                    providedServicesWithCompensationsPayouts.serviceOrganizationSettings.filter(
                        (serviceOrganizationSetting) => servicesInGroup.includes(serviceOrganizationSetting.service.id)
                    );

                for (const serviceProvider of serviceProviders) {
                    if (serviceProvider.service.serviceType === ServiceType.consultation) {
                        specialistServiceProviders.push(serviceProvider);
                    } else {
                        if (serviceProvider.isBiometrician || serviceProvider.isAnalyst) {
                            const existingBiometrician = biometricianServiceProviders.find(
                                (provider) => provider.service.id === serviceProvider.service.id
                            );
                            if (!existingBiometrician) {
                                biometricianServiceProviders.push(serviceProvider);
                                if (serviceProvider.service.code === ServiceCode.TOO) {
                                    tooServiceProvider.push(serviceProvider);
                                }
                            }
                        }
                        if (serviceProvider.isGrader || serviceProvider.isAnalyst) {
                            graderServiceProvider.push(serviceProvider);
                        }
                    }
                }
                if (selectedServiceGroupTab.displayText !== 'Logboek') {
                    tabContents.push(
                        <Fragment key={serviceGroupName}>
                            {renderProvidedActivities(
                                serviceConsumers,
                                CompensationPayoutEntity.serviceConsumer,
                                ActivityType.request
                            )}
                            {renderProvidedActivities(
                                biometricianServiceProviders,
                                CompensationPayoutEntity.serviceProvider,
                                ActivityType.biometrics
                            )}
                            {renderProvidedActivities(
                                tooServiceProvider,
                                CompensationPayoutEntity.serviceProvider,
                                ActivityType.glaucoma
                            )}
                            {renderProvidedActivities(
                                graderServiceProvider,
                                CompensationPayoutEntity.serviceProvider,
                                ActivityType.grading
                            )}
                            {renderProvidedActivities(
                                specialistServiceProviders,
                                CompensationPayoutEntity.serviceProvider,
                                ActivityType.consultation
                            )}
                            {renderProvidedActivities(
                                serviceSupervisors,
                                CompensationPayoutEntity.serviceSupervisor,
                                ActivityType.qualityControl
                            )}
                            {
                                <ServiceActivityCompensationPayouts
                                    compensationPayouts={
                                        providedServicesWithCompensationsPayouts.serviceActivityCompensationPayouts
                                    }
                                    employmentId={employmentId}
                                    episodeCategory={episodeCategory}
                                    higherLevelCompensationPayouts={higherLevelCompensationsPayouts}
                                    onSubmit={async () => await load()}
                                    organizationId={organizationId}
                                    serviceEmployments={serviceEmployments}
                                    serviceOrganizationSettings={serviceOrganizationSettings}
                                />
                            }
                        </Fragment>
                    );
                } else {
                    tabContents.push(
                        <Fragment key={serviceGroupName}>
                            {renderProvidedActivitiesLogs(serviceConsumers, ActivityType.request)}
                            {renderProvidedActivitiesLogs(biometricianServiceProviders, ActivityType.biometrics)}
                            {renderProvidedActivitiesLogs(tooServiceProvider, ActivityType.glaucoma)}
                            {renderProvidedActivitiesLogs(graderServiceProvider, ActivityType.grading)}
                            {renderProvidedActivitiesLogs(specialistServiceProviders, ActivityType.consultation)}
                            {renderProvidedActivitiesLogs(serviceSupervisors, ActivityType.qualityControl)}
                            <ServiceActivityCompensationPayoutsHistory
                                employmentId={employmentId}
                                episodeCategory={episodeCategory}
                                organizationId={organizationId}
                                serviceEmployments={serviceEmployments}
                                serviceOrganizationSettings={
                                    higherLevelCompensationsPayouts?.serviceOrganizationSettings ?? []
                                }
                            />
                        </Fragment>
                    );
                }
            });

        return tabContents;
    }

    function renderProvidedActivitiesLogs(
        compensationPayoutTypes: (IServiceConsumer | IServiceProvider | IServiceSupervisor)[],
        activityType: ActivityType
    ) {
        const compensationPayoutLogs = compensationPayoutTypes.map((compensationPayoutType) => {
            let higherLevelCompensationPayout: ICompensationPayout | undefined;
            if (higherLevelCompensationsPayouts) {
                const serviceOrganizationSetting = higherLevelCompensationsPayouts.serviceOrganizationSettings.find(
                    (item) => item.serviceId === compensationPayoutType.service.id
                );
                if (serviceOrganizationSetting) {
                    // group level
                    higherLevelCompensationPayout = findGroupHigherCompensationPayout(
                        higherLevelCompensationsPayouts.compensationPayouts,
                        activityType,
                        serviceOrganizationSetting
                    );
                }
            }

            return (
                <ActivityCompensationPayoutsHistory
                    activityType={activityType}
                    compensationType={activityType}
                    employmentId={employmentId}
                    episodeCategory={episodeCategory}
                    key={`${activityType}_${compensationPayoutType.id}_${compensationPayoutType.service.serviceType}`}
                    organizationId={organizationId}
                    serviceGroupSettingId={higherLevelCompensationPayout?.serviceGroupSettingId}
                    serviceId={compensationPayoutType.service.id}
                    serviceName={compensationPayoutType.service.name}
                    serviceType={compensationPayoutType.service.serviceType}
                />
            );
        });

        return <>{compensationPayoutLogs}</>;
    }

    function renderProvidedActivities(
        compensationPayoutTypes: (IServiceConsumer | IServiceProvider | IServiceSupervisor)[],
        entity: CompensationPayoutEntity,
        activityType: ActivityType
    ) {
        if (!providedServicesWithCompensationsPayouts) {
            return null;
        }

        if (employmentId) {
            return compensationPayoutTypes.map((compensationPayoutType) => {
                const compensationPayouts = providedServicesWithCompensationsPayouts.compensationPayouts.filter(
                    (compensationPayout) => {
                        return (
                            compensationPayout.type === activityType &&
                            ((entity === CompensationPayoutEntity.serviceConsumer &&
                                compensationPayout.serviceConsumerId === compensationPayoutType.id) ||
                                (entity === CompensationPayoutEntity.serviceProvider &&
                                    compensationPayout.serviceProviderId === compensationPayoutType.id) ||
                                (entity === CompensationPayoutEntity.serviceSupervisor &&
                                    compensationPayout.serviceSupervisorId === compensationPayoutType.id))
                        );
                    }
                );
                let higherLevelCompensationPayout: ICompensationPayout | undefined;
                if (higherLevelCompensationsPayouts) {
                    const serviceOrganizationSetting = higherLevelCompensationsPayouts.serviceOrganizationSettings.find(
                        (item) => item.serviceId === compensationPayoutType.service.id
                    );
                    if (serviceOrganizationSetting) {
                        const today = new Date();
                        // group level
                        higherLevelCompensationPayout = findGroupHigherCompensationPayout(
                            higherLevelCompensationsPayouts.compensationPayouts,
                            activityType,
                            serviceOrganizationSetting
                        );
                        // organization level
                        if (!higherLevelCompensationPayout) {
                            higherLevelCompensationPayout = higherLevelCompensationsPayouts.compensationPayouts.find(
                                (compensationPayout) =>
                                    compensationPayout.type === activityType &&
                                    serviceOrganizationSetting.id === compensationPayout.serviceOrganizationSettingId &&
                                    isActiveOnDate(compensationPayout, today)
                            );
                        }
                    }
                }

                return (
                    <ActivityCompensationPayoutsAddEdit
                        activityType={activityType}
                        compensationPayouts={compensationPayouts}
                        endDate={compensationPayoutType.endDate}
                        episodeCategory={episodeCategory}
                        higherLevelCompensationPayout={higherLevelCompensationPayout}
                        isOnOrganizationLevel={false}
                        key={`${activityType}_${compensationPayoutType.id}`}
                        onSubmit={async () => await load()}
                        organizationId={organizationId}
                        organizationLevelPayoutsStartDate={organization?.organizationLevelPayoutsStartDate}
                        serviceCode={compensationPayoutType.service.code}
                        serviceConsumerId={
                            entity === CompensationPayoutEntity.serviceConsumer ? compensationPayoutType.id : undefined
                        }
                        serviceProviderId={
                            entity === CompensationPayoutEntity.serviceProvider ? compensationPayoutType.id : undefined
                        }
                        serviceSupervisorId={
                            entity === CompensationPayoutEntity.serviceSupervisor
                                ? compensationPayoutType.id
                                : undefined
                        }
                        serviceType={compensationPayoutType.service.serviceType}
                        startDate={compensationPayoutType.startDate}
                        title={getActivityTitle(
                            activityType,
                            compensationPayoutType.service.name,
                            compensationPayoutType.startDate,
                            compensationPayoutType.endDate
                        )}
                    />
                );
            });
        } else if (organizationId) {
            const serviceIds = uniq(
                compensationPayoutTypes.map((compensationPayoutType) => compensationPayoutType.service.id)
            );

            return serviceIds.map((serviceId) => {
                const serviceOrganizationSettings =
                    providedServicesWithCompensationsPayouts.serviceOrganizationSettings.filter(
                        (item) => item.serviceId === serviceId
                    );
                if (serviceOrganizationSettings.length > 0) {
                    return serviceOrganizationSettings.map((serviceOrganizationSetting) => {
                        const compensationPayouts = providedServicesWithCompensationsPayouts.compensationPayouts.filter(
                            (compensationPayout) =>
                                compensationPayout.serviceOrganizationSettingId === serviceOrganizationSetting.id &&
                                compensationPayout.type === activityType
                        );

                        const higherLevelCompensationPayout =
                            higherLevelCompensationsPayouts &&
                            findGroupHigherCompensationPayout(
                                higherLevelCompensationsPayouts.compensationPayouts,
                                activityType,
                                serviceOrganizationSetting
                            );

                        return (
                            <ActivityCompensationPayoutsAddEdit
                                activityType={activityType}
                                compensationPayouts={compensationPayouts}
                                endDate={serviceOrganizationSetting.endDate}
                                episodeCategory={episodeCategory}
                                higherLevelCompensationPayout={higherLevelCompensationPayout}
                                isOnOrganizationLevel={true}
                                key={serviceOrganizationSetting.id}
                                onSubmit={async () => await load()}
                                organizationId={organizationId}
                                organizationLevelPayoutsStartDate={organization?.organizationLevelPayoutsStartDate}
                                serviceCode={serviceOrganizationSetting.service.code}
                                serviceOrganizationSettingId={serviceOrganizationSetting.id}
                                serviceType={serviceOrganizationSetting.service.serviceType}
                                startDate={serviceOrganizationSetting.startDate}
                                title={getActivityTitle(
                                    activityType,
                                    serviceOrganizationSetting.service.name,
                                    serviceOrganizationSetting.startDate,
                                    serviceOrganizationSetting.endDate
                                )}
                            />
                        );
                    });
                }
            });
        }
    }

    function sortByServiceName(providedServices: { service: { name: string } }[]) {
        providedServices.sort((a, b) => {
            if (a.service.name > b.service.name) {
                return 1;
            } else if (a.service.name < b.service.name) {
                return -1;
            } else {
                return 0;
            }
        });
    }

    function getActivityTitle(
        activityType: ActivityType,
        serviceName: string,
        startDate: Date | string,
        endDate?: Date | string
    ) {
        return `${translateKey(`activityTypeEnum.${activityType}`)} ${serviceName} (${DateTime.from(startDate).format(
            'DD-MM-YYYY'
        )} - ${endDate ? DateTime.from(endDate).format('DD-MM-YYYY') : translateKey('common.now').toLowerCase()})`;
    }

    function findGroupHigherCompensationPayout(
        compensationPayouts: ICompensationPayout[],
        activityType: ActivityType,
        serviceOrganizationSetting: IServiceOrganizationSettings
    ) {
        const today = new Date();

        return compensationPayouts.find(
            (compensationPayout) =>
                compensationPayout.type === activityType &&
                compensationPayout.serviceGroupSettingId &&
                serviceOrganizationSetting.serviceOrganizationGroupHistory.length &&
                serviceOrganizationSetting.serviceOrganizationGroupHistory[0].serviceGroupSettingId ===
                    compensationPayout.serviceGroupSettingId &&
                isActiveOnDate(compensationPayout, today) &&
                isActiveOnDate(serviceOrganizationSetting.serviceOrganizationGroupHistory[0], today)
        );
    }
}
